import { HasuraQueries } from "@/api/hasura/queries"
import {
  Complaint_Order_By as ComplaintsOrderBy,
  GetComplaintsQueryVariables,
} from "@/api/hasura/generated/graphql"
import { ComplaintSummary } from "@/api/backofficeRest/generated/model"
import { useQuery } from "@/api/utils/useQuery"
import { IComplaint } from "@/models/complaint/complaint.types"
import { nullsFirstInOrder } from "@/api/utils/hasuraHelpers"
import { ComplaintColumnParser } from "@/models/operations/complaintColumn/complaintColumn.parser"
import dayjs, { Dayjs } from "dayjs"
import {
  COMPLAINT_OPEN_STATUSES,
  MAX_AGE_PROFILE_WEEKS_COUNT,
} from "@/constants/complaints"
import { ComplaintSummaryParser } from "@/models/operations/complaintSummary/complaintSummary.parser"
import { AdminComplaintMutator } from "@/mutators/complaint/AdminComplaintMutator"
import { IDynamicComplaintSummaryDateRaised } from "@/mutators/complaint/ComplaintMutator.types"
import {
  ComplaintMutatorParser,
  emptyDynamicSummary,
} from "@/mutators/complaint/ComplaintMutator.parser"
import { getIntersection } from "@/helpers/arrayHelpers"
import { ComplaintParser } from "@/models/complaint/complaintParser"
import { ComplaintFilterTypes } from "@/views/operations/complaints/opsComplaintConfig"

export type ComplaintQueryResult = {
  records: IComplaint[]
  totalCount: number
}

type WeekCountRange = { from?: number; to: number }

function translateRangesToDates(mergedRanges: WeekCountRange[]) {
  const today = dayjs()

  return mergedRanges.map((range) => {
    const rangeFilter: { _lt?: Dayjs; _gt?: Dayjs } = {
      _lt: today.subtract(range.to, "weeks"),
    }
    if (range.from) {
      rangeFilter._gt = today.subtract(range.from, "weeks")
    }
    return { date_raised: rangeFilter }
  })
}

/**
 * Merges a series of ranges in order to minimize the number of clauses
 * example: mergeConsecutiveRanges([{from:8, to 7}, {from: 7, to: 6}, {from:4, to 3}]) results in
 *  [{from:8, to 6}, {from:4, to 3}]
 * @param weekCountRanges
 */
function mergeConsecutiveRanges(weekCountRanges: WeekCountRange[]) {
  return weekCountRanges?.reduce((acc, current) => {
    if (acc.length === 0) {
      return [current]
    }
    const last = acc[acc.length - 1]

    if (last.to === current.from) {
      last.to = current.to // link 2 consecutive ranges instead of adding them
    } else {
      acc.push(current)
    }

    return acc
  }, [] as WeekCountRange[])
}

/**
 * Remove from field for age range targeting 8+ weeks of age profile
 * @param mergedRanges
 */
function applyOpenEnd(mergedRanges: WeekCountRange) {
  if (mergedRanges.from === MAX_AGE_PROFILE_WEEKS_COUNT + 1) {
    delete mergedRanges.from
  }
}

/**
 * Transforms a set of age profiles values eg: ['1', '5','4', '8'] to a a structure usable in gql
 *
 * @param ageProfiles
 */
export function transformAgeProfilesToDateRanges(ageProfiles?: string[]) {
  if (!ageProfiles?.length) {
    return undefined
  }
  const sortedAgeProfiles = ageProfiles?.map(Number).sort((a, b) => b - a)

  const weekCountRanges = sortedAgeProfiles?.map<WeekCountRange>((to) => ({
    from: to + 1,
    to,
  }))
  const mergedRanges = mergeConsecutiveRanges(weekCountRanges)
  applyOpenEnd(mergedRanges[0])

  const dateRanges = translateRangesToDates(mergedRanges)

  if (ageProfiles.includes("0")) {
    delete dateRanges[dateRanges?.length - 1].date_raised._lt
  }

  return dateRanges
}

const formatListSearch = (value: string[] | undefined) => {
  return {
    _in: value,
  }
}

const formatDateSearch = (from?: Dayjs, to?: Dayjs) => {
  return {
    _gt: from,
    _lt: to,
  }
}

function mapComplaintFilters(filters: ComplaintFilterTypes) {
  const orClauses = transformAgeProfilesToDateRanges(filters.ageProfile)
  const hasAgeProfileFilter = !!orClauses?.length

  const andClauses = hasAgeProfileFilter
    ? { status: formatListSearch(COMPLAINT_OPEN_STATUSES) }
    : {}

  return {
    dateRaised: formatDateSearch(filters.dateRaisedFrom, filters.dateRaisedTo),
    status: formatListSearch(filters.status),
    channel: formatListSearch(filters.channel),
    type: formatListSearch(filters.type),
    subStatus: formatListSearch(filters.subStatus),
    category1: formatListSearch(filters.category1),
    category2: formatListSearch(filters.category2),
    category3: formatListSearch(filters.category3),
    agentName: formatListSearch(filters.agentName),
    team: formatListSearch(filters.team),
    internalCategory: formatListSearch(filters.internalCategory),
    ofgemCategory: formatListSearch(filters.ofgemCategory),
    orClauses: orClauses?.length ? orClauses : {},
    andClauses,
  } satisfies GetComplaintsQueryVariables
}

/**
 * Returns status filter based on existing age profile filter
 *
 * Age profile filter requires an additional AND condition for query ex: status = [Open, Closed] and status =[Open, Reopened, Ombudsman]
 *
 *
 * @param hasAgeProfileFilter
 * @param statusFilter
 *
 * @returns {hasResults: boolean, value: string[]}
 *          {@code hasResults} should be used for early return as the overlap between status filter and age profile filter will return no results
 */
export function prepareStatusFilterForMutation(
  hasAgeProfileFilter: boolean,
  statusFilter: ComplaintFilterTypes["status"],
): { hasResults: boolean; value?: string[] } {
  if (!hasAgeProfileFilter) {
    return { hasResults: true, value: statusFilter }
  }
  if (!statusFilter?.length) {
    return { hasResults: true, value: COMPLAINT_OPEN_STATUSES }
  }

  const intersection = getIntersection(statusFilter, COMPLAINT_OPEN_STATUSES)

  return { hasResults: !!intersection?.length, value: intersection }
}

class ComplaintServiceClass {
  getComplaintList(
    page: number,
    pageSize: number,
    orderBy: ComplaintsOrderBy,
    filters: ComplaintFilterTypes,
  ) {
    if (Object.values(orderBy)[0] === "desc") {
      orderBy = nullsFirstInOrder(orderBy)
    }

    return useQuery({
      ...HasuraQueries.adminComplaint.list(
        (page - 1) * pageSize,
        pageSize,
        orderBy,
        mapComplaintFilters(filters),
      ),
      placeholderData: {
        records: [],
        totalCount: 0,
      },
      useRawResponse: true,
      parserFn: (rawData) => {
        return {
          records: rawData?.complaint?.map((complaint) =>
            ComplaintParser.toDomain(complaint),
          ),
          totalCount: rawData.complaint_aggregate?.aggregate?.count ?? 0,
        }
      },
    })
  }

  async getDynamicComplaintsSummary(filters: ComplaintFilterTypes) {
    const ageProfiles = transformAgeProfilesToDateRanges(
      filters.ageProfile,
    )?.map<IDynamicComplaintSummaryDateRaised>((v) => ({
      gt: v.date_raised._gt?.toISOString(),
      lt: v.date_raised._lt?.toISOString(),
    }))

    const mappedStatusFilter = prepareStatusFilterForMutation(
      !!ageProfiles?.length,
      filters.status,
    )

    if (!mappedStatusFilter.hasResults) {
      // Defaults to empty summary when status filter has invalid combination
      return Promise.resolve(emptyDynamicSummary)
    }

    const result = await AdminComplaintMutator.getDynamicSummary().mutateAsync({
      dateRaised: {
        gt: filters.dateRaisedFrom?.toISOString(),
        lt: filters.dateRaisedTo?.toISOString(),
      },
      status: mappedStatusFilter.value,
      channel: filters.channel,
      type: filters.type,
      subStatus: filters.subStatus,
      category1: filters.category1,
      category2: filters.category2,
      category3: filters.category3,
      agentName: filters.agentName,
      team: filters.team,
      internalCategory: filters.internalCategory,
      ofgemCategory: filters.ofgemCategory,
      ageProfiles,
    })

    return ComplaintMutatorParser.toDomain(result as ComplaintSummary)
  }

  getComplaintsSummary() {
    return useQuery({
      ...HasuraQueries.adminComplaint.summary(),
      useRawResponse: true,
      parserFn: (rawData) => {
        if (!rawData) return undefined
        return ComplaintSummaryParser.toDomain(rawData)
      },
    })
  }

  getUniqueColumnValues() {
    return useQuery({
      ...HasuraQueries.adminComplaint.columnValues(),
      useRawResponse: true,
      parserFn: (rawData) => {
        if (!rawData) return undefined
        return ComplaintColumnParser.toDomain(rawData)
      },
    })
  }
}

export const ComplaintService = new ComplaintServiceClass()
